import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Tooltip from '@mui/material/Tooltip'
import SocialIcon from 'components/SocialIcon'
import { BlueskyFacetsSync } from 'components/Composer/Network/Bluesky'
import { TikTokData } from 'hooks/useTikTokDataFetch'
import { memo } from 'react'
import { FormattedMessage } from 'react-intl'
import { Fragment } from 'react/jsx-runtime'
import { BulkContentPost } from 'services/compose/state'
import { BRAND_BLUESKY, BRAND_PINTEREST } from 'shared/constants'
import { PostDestination } from 'shared/types'
import styles from './BulkPost.pcss'
import { BulkPostActions } from './BulkPostActions'
import { BulkPostMedia } from './BulkPostMedia'
import { BulkPostStatus } from './BulkPostStatus'
import { useArticleLinkDetails } from './hooks/useArticleLinkDetails'
import { useBlueskyImageSizeError } from './hooks/useBlueskyImageSizeError'
import { BulkPostHandlers } from './hooks/useBulkPostHandlers'
import { usePinterestMultiAccountError } from './hooks/usePinterestMultiAccountError'
import { BulkPostNetworkOptions } from './networkOptions/BulkPostNetworkOptions'
import { BulkPostBoards } from './networkOptions/types'
import { isVideoPost } from './postMedia'

const EMPTY_LISTENERS = {}

interface BulkPostProps {
  post: BulkContentPost
  profiles: PostDestination[]
  selected: boolean
  boards: BulkPostBoards
  allowStatusOverride: boolean
  tabIndex?: number
  tiktokData?: Record<string, TikTokData>
  dndAttributes: any
  dndListeners: any
  handlers: BulkPostHandlers
}

export const BulkPost = memo((props: BulkPostProps) => {
  const { post, selected, profiles, dndAttributes, dndListeners, handlers } = props
  const { id, network } = post
  const isVideo = isVideoPost(post)

  useArticleLinkDetails(post)
  usePinterestMultiAccountError(post, profiles.length)
  useBlueskyImageSizeError(post, isVideo)

  const onRecycleChange = (value: number | boolean | Date) => {
    handlers.onRecycleChange(id, value, network)
  }

  const onRecycleApplyToAll = (value: number | boolean | Date) => {
    handlers.onRecycleChange(id, value, network, true)
  }

  return (
    <Fragment>
      <div className={styles['profile-box']}>
        <SocialIcon icon={network} size="32px" circle className={`${styles.icon} ${styles['icon-' + network]}`} />
        <Tooltip
          classes={{ tooltip: styles['tooltip-box'] }}
          title={<div className={styles.tooltip}>{profiles.map(p => p.name).join('\n')}</div>}
        >
          <span className={styles['profiles-label']}>
            <FormattedMessage id="general.profile-selector.multi-profile-selection-label" values={{ count: profiles.length }} />
          </span>
        </Tooltip>
      </div>
      <BulkPostMedia post={post} onMediaClick={handlers.onMediaClick} />
      <BulkPostStatus
        post={post}
        profiles={profiles}
        allowStatusOverride={props.allowStatusOverride}
        tabIndex={props.tabIndex}
        onStatusChange={handlers.onStatusChange}
        onPinChange={handlers.onPinChange}
        onYtStatusChange={handlers.onYtStatusChange}
        onTikTokPhotoStatusChange={handlers.onTikTokPhotoStatusChange}
        onAddComment={handlers.onAddComment}
        onCopyCommentToAll={handlers.onCopyCommentToAll}
      />
      <BulkPostNetworkOptions
        post={post}
        profiles={profiles}
        boards={props.boards}
        tiktokData={props.tiktokData}
        onRecycleChange={onRecycleChange}
        onRecycleApplyToAll={onRecycleApplyToAll}
        onSelectedFBAlbumsChange={handlers.onSelectedFBAlbumsChange}
        onSelectedPIBoardsChange={handlers.onSelectedPIBoardsChange}
        onSelectedIGLocationsChange={handlers.onSelectedIGLocationsChange}
        onUpdateFBReshare={handlers.onUpdateFBReshare}
        onTweetTypeChange={handlers.onTweetTypeChange}
        onToggleFBReshareAll={handlers.onToggleFBReshareAll}
        onApplyTweetTypeToAll={handlers.onApplyTweetTypeToAll}
        onTikTokSettingsChange={handlers.onTikTokSettingsChange}
        onYoutubePrivacyChange={handlers.onYoutubePrivacyChange}
        onAiContentChange={handlers.onAiContentChange}
        onPinterestAiContentModeChange={handlers.onPinterestAiContentModeChange}
        onPostTypeChange={handlers.onPostTypeChange}
        onCoverChange={handlers.onCoverChange}
      />
      <BulkPostActions
        postId={id}
        selected={selected}
        dndAttributes={dndAttributes}
        dndListeners={dndListeners}
        onMoveToTop={handlers.onMoveToTop}
        onMoveToBottom={handlers.onMoveToBottom}
        onRemove={handlers.onRemove}
        onToggleSelected={handlers.onToggleSelected}
      />
      {
        post.error && (
          <div className={styles['error-box']}>
            {post.error.message}
          </div>
        )
      }
      {network === BRAND_BLUESKY && <BlueskyFacetsSync multipostId={id} text={post.data.status.text} />}
    </Fragment>
  )
})

export const SortableBulkPost = memo((props: Omit<BulkPostProps, 'dndListeners' | 'dndAttributes'>) => {
  const { post } = props
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: post.id })

  // dnd-kit memoizes both, so the memoized BulkPost subtree does not re-render on unrelated renders.
  const dndListeners = listeners || EMPTY_LISTENERS

  const style = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? '2' : '1',
    transition,
    position: isDragging ? 'relative' : 'static'
  } as any

  const articleClassName = post.article ? styles.article : ''
  const pinClassName = post.network === BRAND_PINTEREST ? styles.pin : ''
  const errorClassName = post.error ? styles.error : ''

  return (
    <div className={`${styles.outerContainer} ${errorClassName}`}>
      <div
        className={`${styles.container} ${articleClassName} ${pinClassName}`}
        ref={setNodeRef}
        style={style}
        data-testid="bulk-post"
      >
        <BulkPost {...props} dndAttributes={attributes} dndListeners={dndListeners} />
      </div>
    </div>
  )
})

export default BulkPost
